import React, {useEffect, useMemo, useState} from 'react';
import ContentPasteOffRoundedIcon from '@mui/icons-material/ContentPasteOffRounded';
import {useNavigate, useOutletContext} from 'react-router-dom';
import AdmicityStatus from '../../../../shared-components/AdmicityStatus';
import {getFormRequestStatusInfo} from '../common/common';
import {
    useGetMyFormsQuery,
    useGetMyCommunicationFormsQuery,
    useGetMyNotificationFormsQuery,
    useGetMyLinkedSelfServiceFormsQuery,
    useLazyGetMyNotificationFormResponsesQuery,
    useClaimFormRequestMutation,
    useUnclaimFormRequestMutation
} from '../../../../api/services/formRequestsService';
import AdmicityTableV2 from '../../../../shared-components/Table/V2/AdmicityTableV2';
import {formatDate, formatDateTime, formatFormExpirationDate} from '../../../../utility/dateUtil';
import withPaginationV2 from '../../../../hoc/pagination/withPaginationV2';
import {
    downloadFormResponsePdfReport,
    useLazyGenerateFormResponsesReportByStudentQuery
} from '../../../../api/services/filesService';
import {useGetEmployeeByCurrentUserQuery} from '../../../../api/services/employeeService';
import useFileDownloader from '../../../../utility/hooks/useFileDownloader';
import useNotification from '../../../../utility/hooks/useNotification';
import {FORM_REQUEST_STATUSES} from '../../../../constants/formRequestStatuses';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import useUser from '../../../../utility/hooks/useUser';
import {ROLES} from '../../../../constants/roles';
import NotificationFormsTable from '../../../../shared-components/NotificationFormsTable';
import AdmicityTabs from '../../../../shared-components/AdmicityTabs';
import Paper from '@mui/material/Paper';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import FILTER_OPERATIONS from '../../../../constants/filterOperations';
import ApprovalFormsTable from '../../../../shared-components/ApprovalFormsTable';
import {getApprovalStatusInfo} from '../../../../constants/FormApprovalStatuses';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {Tooltip, IconButton, Box, TextField, InputAdornment} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import useDebounce from '../../../../utility/hooks/useDebounce';

const expirationHeader = (
    <Box sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5
    }}>
        <span>Expiration Date</span>
        <Tooltip title="Expiration date and time are shown in your local time">
            <IconButton
                size="small"
                tabIndex={-1}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <InfoOutlinedIcon fontSize="inherit"/>
            </IconButton>
        </Tooltip>
    </Box>
);

/* eslint-disable react/prop-types */
const allLinkedEmployeesCanSubmit = (row) => {
    const rowData = row?.original ?? row;
    return !!(rowData?.form?.allLinkedEmployeesCanSubmit ?? rowData?.form?.AllLinkedEmployeesCanSubmit);
};

const parentBaseColumns = [
    {
        accessorKey: 'formName',
        header: 'Form Name'
    },
    {
        id: 'firstName',
        header: 'First Name',
        accessorFn: (row) => row?.student?.forename || row?.employee?.forename || '',
    },
    {
        id: 'lastName',
        header: 'Last Name',
        accessorFn: (row) => row?.student?.legalSurname || row?.employee?.legalSurname || '',
    },
    {
        accessorKey: 'status',
        header: 'Status',
        Cell: ({cell}) => <AdmicityStatus {...getFormRequestStatusInfo(cell.getValue())} />,
    },
    {
        accessorKey: 'expirationDate',
        header: expirationHeader,
        Cell: ({cell}) => (cell.getValue() ? formatFormExpirationDate(cell.getValue()) : ''),
    },
];

const claimColumns = [
    {
        accessorKey: 'claimedBy',
        header: 'Claimed By',
        enableColumnFilter: false,
        Cell: ({
                   row,
                   cell
               }) =>
            allLinkedEmployeesCanSubmit(row) ? '' : cell.getValue(),
    },
    {
        accessorKey: 'claimedAt',
        header: 'Claimed At',
        Cell: ({
                   row,
                   cell
               }) =>
            allLinkedEmployeesCanSubmit(row)
                ? ''
                : (cell.getValue() ? formatDate(cell.getValue()) : ''),
        enableColumnFilter: false,
    },
];

const parentSubmittedAndApprovalColumns = [
    {
        id: 'editedBy',
        accessorFn: (row) => row?.editedBy?.fullname,
        header: 'Submitted By',
    },
    {
        accessorKey: 'editedAt',
        header: 'Submitted At',
        Cell: ({cell}) => (cell.getValue() ? formatDate(cell.getValue()) : ''),
    },
    {
        accessorKey: 'approvalStage',
        header: 'Approval Stage',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvalStageProgress',
        header: 'Approval Progress',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvalStatus',
        header: 'Approval Status',
        Cell: ({cell: {getValue}}) => (
            <AdmicityStatus {...getApprovalStatusInfo(getValue())} />
        ),
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvedBy',
        header: 'Approved By',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvedAt',
        header: 'Approved At',
        Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : '',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvalNote',
        header: 'Approval Note',
        enableColumnFilter: false,
    },
];

const tableColumnsByRole = {
    PARENT: [
        ...parentBaseColumns,
        ...parentSubmittedAndApprovalColumns
    ],
    PARENT_STUDENT_FORMS: [
        ...parentBaseColumns,
        ...claimColumns,
        ...parentSubmittedAndApprovalColumns
    ],
    DEFAULT: [
        {
            accessorKey: 'formName',
            header: 'Form Name'
        },
        {
            accessorKey: 'status',
            header: 'Status',
            Cell: ({cell}) => <AdmicityStatus {...getFormRequestStatusInfo(cell.getValue())} />,
        },
        {
            accessorKey: 'expirationDate',
            header: expirationHeader,
            Cell: ({cell}) => (cell.getValue() ? formatFormExpirationDate(cell.getValue()) : ''),
        },
    ],
};

const generalFormsTableColumns = [
    {
        accessorKey: 'formName',
        header: 'Form Name',
        filterFn: FILTER_OPERATIONS.CONTAINS
    }
];

const communicationFormsTableColumns = [
    {
        accessorKey: 'formName',
        header: 'Form Name',
        filterFn: FILTER_OPERATIONS.CONTAINS,
    },
    {
        accessorKey: 'sentAt',
        header: 'Date',
        Cell: ({cell}) => {
            const date = cell.getValue();
            return date ? formatDate(date) : '';
        },
        enableColumnFilter: false,
    },
];

/* eslint-enable react/prop-types */

const linkedSelfServiceFormsColumns = [
    {
        accessorKey: 'name',
        header: 'Form Name',
        filterFn: FILTER_OPERATIONS.CONTAINS
    },
    {
        accessorKey: 'linkedPersonName',
        header: 'Linked To',
        filterFn: FILTER_OPERATIONS.CONTAINS
    },
    {
        accessorKey: 'lastSubmittedBy',
        header: 'Last Completed By',
        enableColumnFilter: false
    },
    {
        accessorKey: 'lastSubmittedAt',
        header: 'Last Completed',
        Cell: ({cell}) => cell.getValue() ? formatDateTime(cell.getValue()) : 'Not yet completed',
        enableColumnFilter: false
    }
];

const RequestAndResponses = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(`${theme.breakpoints.down('sm')}, (max-height:600px)`);
    const navigate = useNavigate();
    const downloadFile = useFileDownloader();
    const {
        showSuccessNotification,
        showErrorNotification
    } = useNotification();
    const {user} = useUser();
    const shouldAddActions = user.hasRoles([
        ROLES.EMPLOYEE,
        ROLES.PARENT
    ]);
    const [generateFormResponsesReportByStudent] = useLazyGenerateFormResponsesReportByStudentQuery();
    const [claimFormRequest, {isLoading: isClaimingFormRequest}] = useClaimFormRequestMutation();
    const [unclaimFormRequest, {isLoading: isUnclaimingFormRequest}] = useUnclaimFormRequestMutation();
    const {
        key,
        formsState,
        setFormsState
    } = useOutletContext();
    const [currentTabIndex, setCurrentTabIndex] = useState(formsState?.currentTabIndex ?? 0);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search.trim(), 400);
    const searchArgs = useMemo(() => (debouncedSearch ? {search: debouncedSearch} : {}), [debouncedSearch]);
    const [selectedNotificationFormIndex, setSelectedNotificationFormIndex] = useState(formsState?.selectedNotificationFormIndex);
    const {
        data: notificationForms = [],
        isLoading: isLoadingNotificationForms,
        isFetching: isFetchingNotificationForms,
    } = useGetMyNotificationFormsQuery(undefined, {
        skip: !user.hasRole(ROLES.EMPLOYEE) || currentTabIndex !== 2
    });

    const [selectedLinkedFormIndex, setSelectedLinkedFormIndex] = useState(undefined);
    const {
        data: linkedSelfServiceForms = [],
        isLoading: isLoadingLinkedForms,
        isFetching: isFetchingLinkedForms,
    } = useGetMyLinkedSelfServiceFormsQuery(undefined, {
        skip: !user.hasRole(ROLES.EMPLOYEE) || currentTabIndex !== 3
    });

    const {data: currentEmployee} = useGetEmployeeByCurrentUserQuery(undefined, {
        skip: !user.hasRole(ROLES.EMPLOYEE),
    });

    const TableV2 = useMemo(() => withPaginationV2(AdmicityTableV2, {
        func: useGetMyFormsQuery,
        props: {
            linkToStudents: false
        }
    }), []);

    const StudentFormsTableV2 = useMemo(() => withPaginationV2(AdmicityTableV2, {
        func: useGetMyFormsQuery,
        props: {
            linkToStudents: true
        }
    }), []);

    const CommunicationFormsTable = useMemo(() => withPaginationV2(AdmicityTableV2, {
        func: useGetMyCommunicationFormsQuery
    }), []);

    useEffect(() => {
        return () => {
            setFormsState({
                currentTabIndex,
                selectedNotificationFormIndex
            });
        };
    }, [setFormsState, currentTabIndex, selectedNotificationFormIndex]);

    const getRowData = (row) => row?.original ?? row;

    const isClaimedByCurrentEmployee = (row) => {
        const rowData = getRowData(row);
        return !!rowData.claimedByEmployeeId && String(rowData.claimedByEmployeeId) === String(currentEmployee?.id);
    };

    const isClaimedByAnotherEmployee = (row) => {
        const rowData = getRowData(row);
        return !!rowData.claimedByEmployeeId && String(rowData.claimedByEmployeeId) !== String(currentEmployee?.id);
    };
    const isSubmittedFormRequest = (row) => getRowData(row).status === FORM_REQUEST_STATUSES.submitted;
    const handleClaimFormRequest = async (formRequestId) => {
        try {
            await claimFormRequest({
                formRequestId,
                employeeId: currentEmployee?.id
            }).unwrap();

            showSuccessNotification('Form claimed');
        } catch (error) {
            showErrorNotification(error?.message || 'Failed to claim form request');
        }
    };

    const handleUnclaimFormRequest = async (formRequestId) => {
        try {
            await unclaimFormRequest({
                formRequestId,
                employeeId: currentEmployee?.id
            }).unwrap();

            showSuccessNotification('Form unclaimed');
        } catch (error) {
            showErrorNotification(error?.message || 'Failed to unclaim form request');
        }
    };

    const downloadPdfReport = async (formRequestId = null, formId = null, isBulk = true) => {
        if (shouldAddActions) {
            if (formRequestId && !isBulk) {
                await downloadFile(
                    downloadFormResponsePdfReport,
                    {
                        formId,
                        formRequestId
                    }
                );
            } else {
                await generateFormResponsesReportByStudent({
                    formId,
                    allStudents: true
                });
                showSuccessNotification(
                    'The PDFs is being created. It will be sent to your email address when the process is complete.'
                );
            }
        }
    };

    const toolbarIconActions = shouldAddActions
        ? [{
            icon: <FileDownloadRoundedIcon/>,
            title: 'Download report',
            type: 'select',
            hideIfEmpty: true,
            menuItems: [
                {
                    label: 'Download PDF',
                    onClick: downloadPdfReport
                }
            ]
        }]
        : [];

    const rowActions = shouldAddActions
        ? [
            {
                label: 'Download PDF',
                action: params => downloadPdfReport(params.id, params.form.id, false),
                disabled: row => row.status !== FORM_REQUEST_STATUSES.submitted,
            }
        ]
        : [];

    const tabs = [
        {
            label: 'Forms',
            component: <TableV2
                variant="elevation"
                id={key}
                queryArgs={user.hasRoles([ROLES.EMPLOYEE, ROLES.PARENT]) ? searchArgs : undefined}
                columns={shouldAddActions ? tableColumnsByRole.PARENT : tableColumnsByRole.DEFAULT}
                tableProps={{
                    enableSorting: false,
                    enableFilters: false,
                    enableFullScreenToggle: false
                }}
                toolbarIconActions={toolbarIconActions}
                rowActions={rowActions}
                onRowClick={(params) => navigate(`${params.id}`)}
                noRowsOverlay={{
                    icon: <ContentPasteOffRoundedIcon/>,
                    text: 'There are no forms to show'
                }}
            />
        },
        ...(user.hasRole(ROLES.EMPLOYEE)
            ? [
                {
                    label: 'Student Forms',
                    component: <StudentFormsTableV2
                        variant="elevation"
                        id={`${key}/student-forms`}
                        queryArgs={searchArgs}
                        columns={shouldAddActions ? tableColumnsByRole.PARENT_STUDENT_FORMS : tableColumnsByRole.DEFAULT}
                        tableProps={{
                            enableSorting: false,
                            enableFilters: false,
                            enableFullScreenToggle: false
                        }}
                        toolbarIconActions={toolbarIconActions}
                        rowActions={[
                            {
                                label: 'Claim',
                                action: params => handleClaimFormRequest(params.id),
                                hidden: row => allLinkedEmployeesCanSubmit(row) || isSubmittedFormRequest(row),
                                disabled: row => {
                                    const rowData = getRowData(row);

                                    return isClaimedByCurrentEmployee(rowData) ||
                                        isClaimedByAnotherEmployee(rowData) ||
                                        isClaimingFormRequest ||
                                        isUnclaimingFormRequest;
                                }
                            },
                            {
                                label: 'Unclaim',
                                action: params => handleUnclaimFormRequest(params.id),
                                hidden: row => allLinkedEmployeesCanSubmit(row) || isSubmittedFormRequest(row),
                                disabled: row => {
                                    const rowData = getRowData(row);

                                    return !isClaimedByCurrentEmployee(rowData) ||
                                        isClaimingFormRequest ||
                                        isUnclaimingFormRequest;
                                }
                            },
                            ...(shouldAddActions
                                ? [
                                    {
                                        label: 'Download PDF',
                                        action: params => downloadPdfReport(params.id, params.form.id, false),
                                        disabled: row => {
                                            const rowData = getRowData(row);
                                            return rowData.status !== FORM_REQUEST_STATUSES.submitted;
                                        },
                                    }
                                ]
                                : [])
                        ]}
                        onRowClick={(params) => navigate(`${params.id}`)}
                        noRowsOverlay={{
                            icon: <ContentPasteOffRoundedIcon/>,
                            text: 'There are no student forms to show'
                        }}
                    />
                },
                {
                    label: 'Self-service Forms',
                    component:
                        <NotificationFormsTable
                            id={`${key}/general`}
                            data={notificationForms}
                            columns={generalFormsTableColumns}
                            isLoading={isLoadingNotificationForms || isFetchingNotificationForms}
                            selectedRowIndex={selectedNotificationFormIndex}
                            setSelectedRowIndex={setSelectedNotificationFormIndex}
                            navigate={navigate}
                            personId={null}
                            fetchResponsesFn={useLazyGetMyNotificationFormResponsesQuery}
                        />
                },
                {
                    label: 'Linked Self-service Forms',
                    component:
                        <NotificationFormsTable
                            id={`${key}/linked`}
                            data={linkedSelfServiceForms}
                            columns={linkedSelfServiceFormsColumns}
                            isLoading={isLoadingLinkedForms || isFetchingLinkedForms}
                            selectedRowIndex={selectedLinkedFormIndex}
                            setSelectedRowIndex={setSelectedLinkedFormIndex}
                            navigate={navigate}
                            personId={null}
                            fetchResponsesFn={useLazyGetMyNotificationFormResponsesQuery}
                        />
                },
                {
                    label: 'Approvals',
                    component: currentEmployee?.id
                        ? <ApprovalFormsTable
                            employeeId={currentEmployee.id}
                            navigate={navigate}
                        />
                        : null
                }
            ]
            : []),
        {
            label: 'Communications',
            component: <CommunicationFormsTable
                variant="elevation"
                id={`${key}/communication`}
                columns={communicationFormsTableColumns}
                tableProps={{
                    enableSorting: false,
                    enableRowActions: true,
                    enableFullScreenToggle: false
                }}
                onRowClick={(params) => navigate(`${params.requestId}`)}
                noRowsOverlay={{
                    icon: <ContentPasteOffRoundedIcon/>,
                    text: 'No communication yet'
                }}
            />
        }
    ];

    return (
        <Paper
            variant="outlined"
            sx={
                isSmallScreen
                    ? {}
                    : {
                        minHeight: '100%',
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column'
                    }}
        >
            {/* Everyone who receives forms can search them: employees and parents (AD-2638). */}
            {user.hasRoles([ROLES.EMPLOYEE, ROLES.PARENT]) && currentTabIndex <= 1 && (
                <TextField
                    size="small"
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                    placeholder="Search by form or name"
                    inputProps={{'aria-label': 'Search my forms', 'data-testid': 'my-forms-search'}}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchRoundedIcon fontSize="small"/>
                            </InputAdornment>
                        )
                    }}
                    sx={{m: 2, mb: 0, width: {xs: 'calc(100% - 32px)', sm: 360}}}
                />
            )}
            {
                tabs.length > 1
                    ? <AdmicityTabs
                        tabs={tabs}
                        tabIndex={currentTabIndex}
                        onChange={(index) => {
                            setCurrentTabIndex(index);
                            setSelectedNotificationFormIndex(undefined);
                        }}
                        displayDivider
                        slots={{
                            panel: {
                                paddingRight: 0,
                                paddingLeft: 0
                            }
                        }}
                    />
                    : tabs[0].component

            }

        </Paper>
    );
};

export default RequestAndResponses;
